import type { OrderDTO, ShipmentDTO } from 'ecosystem'

/**
 * The canonical carrier fields for a DEFAULT-shipping selection.
 *
 * The DEFAULT branch has no third-party carrier, so there is no real carrier
 * name to record. We still mirror the shopper's chosen method onto the
 * canonical fields — `carrierService` = the method id, `carrierName` = its
 * merchant-facing label — so fulfilment, the confirmation page and the order
 * detail card can answer "which option did they pick" without reading
 * `additionalInfo.defaultDetails`. Returns `{}` when the store offers only the
 * implicit single option, since there is no choice worth recording.
 *
 * Used by every DEFAULT writer — the UPFRONT path
 * (`updateDefaultShipmentHandler`, `buildShipmentFromDefaultDetails`) and the
 * invoice path (`InvoiceDefault`) — so all three agree.
 */
export const carrierFieldsFromSelectedMethod = (
  selected?: { id: string; label: string } | null
): Pick<ShipmentDTO, 'carrierName' | 'carrierService'> =>
  selected ? { carrierService: selected.id, carrierName: selected.label } : {}

/**
 * True when a `ShipmentDTO` carries enough data to render in the UI.
 * Used to filter empty placeholder shipments (e.g. an OM-pending
 * `shipments[]` row with only an id) before the order detail / confirmation
 * page decides between live and intent-derived display.
 */
export const hasShipmentData = (s: ShipmentDTO): boolean =>
  Boolean(
    s.status ||
    s.carrierName ||
    s.carrierService ||
    s.trackingNumber ||
    s.trackingUrl ||
    s.locationName ||
    s.shippedDate ||
    s.deliveryDate ||
    s.estimatedDeliveryDate ||
    s.items?.length
  )

/**
 * Build a synthetic `ShipmentDTO` from an order's root-level shipping
 * fields. Used when the live `shipments[]` array is empty (DRAFT orders,
 * orders OM hasn't acknowledged yet, legacy upfront orders) so the UI
 * can still render the shopper's checkout intent.
 *
 * Returns `null` when there's no shipping info to render.
 */
export const shipmentFromOrderRoot = (order: OrderDTO): ShipmentDTO | null => {
  const info = order.shippingInfo
  if (!info) return null
  const synthetic: ShipmentDTO = {
    shippingProvider: order.shippingProvider,
    shipmentId: order.shippingId,
    shippingType: info.shippingType,
    carrierName: info.carrierName,
    carrierService: info.carrierService,
    trackingNumber: info.trackingNumber,
    trackingUrl: info.trackingUrl,
    locationId: info.pickupLocationId,
    locationName: info.pickupLocationName,
    address: info.pickupAddress ?? order.shippingAddress,
    estimatedDeliveryDate: info.estimatedDeliveryDate,
    shippedDate: info.shippedDate,
    deliveryDate: info.deliveryDate,
    items: order.items?.map((i) => ({ productId: i.product.id, quantity: i.quantity }))
  }
  return hasShipmentData(synthetic) ? synthetic : null
}

/**
 * Pick the shipments to render for an order. Prefers the live
 * `order.shipments[]` (OM-populated state, including operator splits);
 * falls back to a synthetic shipment built from the order's root-level
 * fields when the live array is empty. Returns `[]` when neither yields
 * displayable data.
 */
export const pickShipmentsForDisplay = (order: OrderDTO): ShipmentDTO[] => {
  const live = (order.shipments ?? []).filter(hasShipmentData)
  if (live.length) return live
  const synthetic = shipmentFromOrderRoot(order)
  return synthetic ? [synthetic] : []
}
